<template>
    <v-layout>
        <v-app-bar
            class="app-navbar position-fixed px-2 px-md-8"
            theme="primary"
        >
            <template #prepend>
                <router-link to="/" class="app-image-container">
                    <img
                        src="/images/odg-icon@144.png"
                        alt="ODG logo"
                    />
                </router-link>
            </template>

            <template #title>
                <div class="d-flex align-center ga-2">
                    <router-link to="/" class="text-white text-decoration-none app-name">
                        <span class="d-none d-sm-inline">Ona Druga Grupa</span>
                        <span class="d-inline d-sm-none">ODG</span>
                    </router-link>

                    <v-btn
                        icon
                        color="white"
                        density="compact"
                        size="small"
                        to="/ona-druga-grupa"
                        class="hidden-sm-and-up"
                        aria-label="About"
                        data-testid="about-button-icon"
                    >
                        <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                </div>
            </template>

            <template v-if="$vuetify.display.smAndUp" #default>
                <div class="d-flex ga-1 mr-4">
                    <v-btn
                        v-prefetch="'/ona-druga-grupa'"
                        to="/ona-druga-grupa"
                        prepend-icon="mdi-information-outline"
                        data-testid="about-button"
                    >
                        <span class="hidden-sm-and-down">O nama</span>
                    </v-btn>

                    <v-btn
                        v-prefetch="'/najnovije-igre'"
                        to="/najnovije-igre"
                        prepend-icon="mdi-dice-d20"
                        data-testid="games-button"
                    >
                        <span class="hidden-sm-and-down">Igre</span>
                    </v-btn>

                    <v-btn
                        v-prefetch="'/objave'"
                        to="/objave"
                        prepend-icon="mdi-newspaper"
                        data-testid="announcements-button"
                    >
                        <span class="hidden-sm-and-down">Objave</span>
                    </v-btn>

                    <v-btn
                        v-prefetch="'/kalendar-igara'"
                        to="/kalendar-igara"
                        prepend-icon="mdi-calendar"
                        data-testid="calendar-button"
                    >
                        <span class="hidden-sm-and-down">Kalendar</span>
                    </v-btn>

                    <v-btn
                        v-prefetch="'/arhiva'"
                        to="/arhiva"
                        prepend-icon="mdi-history"
                        data-testid="archive-button"
                    >
                        <span class="hidden-sm-and-down">Arhiva</span>
                    </v-btn>

                    <v-btn
                        v-if="store.isAdmin"
                        v-prefetch="'/admin'"
                        to="/admin"
                        prepend-icon="mdi-crown"
                        data-testid="admin-button"
                    >
                        <span class="hidden-sm-and-down">Admin</span>
                    </v-btn>
                </div>
            </template>

            <template #append>
                <UserMenu />
            </template>
        </v-app-bar>
        <v-main>
            <v-container :max-width="isWideRoute ? '100%' : 1024">
                <router-view v-if="!store.isLoading" />
            </v-container>
        </v-main>

        <v-bottom-navigation class="d-sm-none position-fixed bottom-nav">
            <v-btn
                v-prefetch="'/'"
                to="/"
                aria-label="Home"
                data-testid="home-button"
            >
                <v-icon>mdi-home</v-icon>
            </v-btn>

            <v-btn
                v-prefetch="'/najnovije-igre'"
                to="/najnovije-igre"
                aria-label="Games"
                data-testid="games-button"
            >
                <v-icon>mdi-dice-d20</v-icon>
            </v-btn>

            <v-btn
                v-show="!store.isAdmin"
                v-prefetch="'/objave'"
                to="/objave"
                aria-label="Announcements"
                data-testid="announcements-button"
            >
                <v-icon>mdi-newspaper</v-icon>
            </v-btn>

            <v-btn
                v-prefetch="'/kalendar-igara'"
                to="/kalendar-igara"
                aria-label="Event calendar"
                data-testid="calendar-button"
            >
                <v-icon>mdi-calendar</v-icon>
            </v-btn>

            <v-btn
                v-prefetch="'/arhiva'"
                to="/arhiva"
                aria-label="Archive"
                data-testid="archive-button"
            >
                <v-icon>mdi-history</v-icon>
            </v-btn>

            <v-btn
                v-if="store.isAdmin"
                v-prefetch="'/admin'"
                to="/admin"
                aria-label="Admin controls"
                data-testid="admin-button"
            >
                <v-icon>mdi-crown</v-icon>
            </v-btn>
        </v-bottom-navigation>

        <v-snackbar
            :model-value="notification.hasNotification"
            :color="notificationColor"
            :timeout="-1"
            :vertical="notification.confirmAction != null || notification.cancelAction != null"
            :multi-line="notification.confirmAction != null || notification.cancelAction != null"
            class="mx-auto"
            data-testid="notification"
            @update:model-value="onSnackbarModelChange"
        >
            {{ notification.message }}

            <template #actions>
                <div class="d-flex align-center ga-2">
                    <v-btn
                        v-if="notification.cancelAction"
                        text
                        color="danger"
                        data-testid="notification-cancel-button"
                        @click="processCancelAction()"
                    >
                        {{ notification.cancelAction.label }}
                    </v-btn>
                    <v-btn
                        v-if="notification.confirmAction"
                        text
                        data-testid="notification-action-button"
                        @click="processConfirmAction()"
                    >
                        {{ notification.confirmAction.label }}
                    </v-btn>
                    <v-btn
                        icon="mdi-close"
                        variant="text"
                        density="comfortable"
                        size="small"
                        aria-label="Zatvori obavijest"
                        data-testid="notification-close-button"
                        @click="dismiss('manual')"
                    />
                </div>
            </template>
        </v-snackbar>
    </v-layout>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { useNotifications } from '../../composition/useNotifications';
import { ModalCode } from '../../modals/ModalCode';
import { useModal } from '../../modals/useModal';
import { hasDoneOnboarding } from '../../services/OnboardingService';
import { useUserStore } from '../../stores/userStore';
import { useRoute } from 'vue-router';
import { Route } from '../../routes/routes';
import { useForceChangePassword } from '../../composition/useForceChangePassword';
import UserMenu from './UserMenu.vue';

const store = useUserStore();
const route = useRoute();
const { showModal } = useModal();

useForceChangePassword();

const {
    notification,
    notificationColor,
    dismiss,
} = useNotifications();

function processCancelAction() {
    if (notification.cancelAction?.callback) {
        notification.cancelAction.callback();
    }
    dismiss('cancel_action');
}

function processConfirmAction() {
    if (notification.confirmAction?.callback) {
        notification.confirmAction.callback();
    }
    dismiss('confirm_action');
}

// Catch-all for cases where Vuetify itself decides to flip the model (e.g. a
// swipe gesture). The composable's own dismiss paths are idempotent, so the
// echo emitted right after they set hasNotification = false is a safe no-op.
function onSnackbarModelChange(value: boolean) {
    if (!value) {
        dismiss('manual');
    }
}

const wideRoutes = new Set([Route.Memberships, Route.Users]);
const isWideRoute = computed(() => {
    return wideRoutes.has(route.name as Route);
});

onMounted(() => {
    if (!hasDoneOnboarding()) {
        showModal(ModalCode.Onboarding);
    }
});
</script>

<style lang="scss" scoped>
.app-navbar {
    :deep(.v-toolbar-title) {
        margin-left: 12px;
        font-family: 'Inter Variable', 'Inter', sans-serif;
        font-size: 18px;
        font-weight: 600;
        letter-spacing: -0.15px;
    }
}

.app-image-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background-color: rgba(var(--v-theme-on-background));

    img {
        width: 38px;
        height: 38px;
    }
}

.bottom-nav :deep(.v-bottom-navigation__content > .v-btn) {
    @media (max-width: 400px) {
        flex: 1;
        min-width: 0;
        padding: 0 4px;
    }
}
</style>
